'use client';

import React, { cloneElement, useCallback } from 'react';
import { FormLabels, generateFieldId, isRequired as isRequiredField } from '@utils';
import { Key } from 'react-aria-components';
import {
	Control,
	Controller,
	ControllerRenderProps,
	FieldPath,
	FieldValues,
} from 'react-hook-form';
import * as Yup from 'yup';

interface ChildComponentProps {
	options?: Array<{ value: string; label: string }>;
	items?: Array<{ value: string; label: string }>;
	[key: string]: unknown;
}

interface ControllerPlusProps<T extends FieldValues> {
	control: Control<T>;
	name: string;
	schema: Yup.AnyObject;
	labels: FormLabels<T>;
	shortLabels?: FormLabels<T>;
	formValues: FieldValues;
	children: React.ReactElement<ChildComponentProps>;
	formContext?: unknown;
	isDisabled?: boolean;
	onChange?: (event: unknown, field: ControllerRenderProps<T, FieldPath<T>>) => void;
	isEditing?: boolean;
	hideLabel?: boolean;
}

export const ControllerPlus = <T extends FieldValues,>({
	control,
	name,
	schema,
	labels,
	shortLabels,
	formValues,
	children,
	formContext,
	isDisabled,
	onChange,
	isEditing = true,
	hideLabel = false,
}: ControllerPlusProps<T>) => {
	const extractLastFieldName = (name: string) => {
		return name.toString().includes('.') ? name.split('.').reverse()[0] : name;
	};

	const valueOnReadMode = useCallback(
		(value: string | undefined) => {
			let result = value;
			type Option = { value: string; label: string };
			if (!isEditing) {
				const optionsOrItems = children.props.options || children.props.items;
				if (optionsOrItems) {
					const valueOption = (optionsOrItems as Option[]).find(option => option.value === value);
					result = valueOption?.label ?? value;
				}
			}
			return result;
		},
		[isEditing, children.props.options, children.props.items],
	);

	return (
		<Controller
			control={control}
			name={name as FieldPath<T>}
			render={({ field, fieldState }) => {
				const isRequired = isRequiredField(field.name as keyof T, schema, formValues, formContext);
				const fieldId = generateFieldId(field.name);
				const labelKeyName = extractLastFieldName(field.name);
				const isFieldDisabled = field.disabled || isDisabled;

				/**
				 * Allow overriding the default onChange handler
				 * @param event
				 */
				const handleChange = (event: unknown) => {
					if (onChange) {
						onChange(event, field);
					} else {
						field.onChange(event);
					}
				};

				return (
					<div className="flex flex-col gap-1 break-words">
						{!hideLabel && !isEditing && (
							<p className="text-caption">
								{shortLabels
									? shortLabels[labelKeyName as keyof T]
									: labels[labelKeyName as keyof T]}
							</p>
						)}
						{isEditing ? (
							cloneElement(children, {
								name: field.name,
								value: field.value,
								defaultValue: field.value,
								selectedKey: field.value as Key,
								label: isEditing && !hideLabel ? labels[labelKeyName as keyof T] : undefined,
								handleChange: handleChange,
								onChange: handleChange,
								onSelectionChange: handleChange,
								errorMessage: fieldState.error?.message,
								isDisabled: isFieldDisabled,
								isRequired: isRequired,
								isInvalid: fieldState.invalid,
								accessibilityProps: {
									id: fieldId,
									name: field.name,
									isRequired: isRequired,
									isDisabled: isFieldDisabled,
								},
							} as Record<string, unknown>)
						) : (
							<h4 className="text-light-text md:text-lg md:font-bold">
								{valueOnReadMode(field.value)}
							</h4>
						)}
					</div>
				);
			}}
		/>
	);
};
